import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, MessageSquare, Mail, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'in_person', label: 'In Person', icon: User }
];

const DIRECTIONS = ['inbound', 'outbound'];
const STATUSES = ['draft', 'sent', 'delivered', 'failed'];

const channelColors = {
  whatsapp: 'bg-green-100 text-green-800',
  sms: 'bg-blue-100 text-blue-800',
  email: 'bg-purple-100 text-purple-800',
  phone: 'bg-amber-100 text-amber-800',
  in_person: 'bg-slate-100 text-slate-700'
};

export default function CommunicationsPage() {
  const [logs, setLogs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    lead_id: '',
    channel: 'email',
    direction: 'outbound',
    subject: '',
    content: '',
    recipient_phone: '',
    recipient_email: '',
    template_name: '',
    status: 'sent'
  });

  useEffect(() => {
    fetchLogs();
    fetchLeads();
  }, [channelFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = async () => {
    try {
      const url = channelFilter === 'all'
        ? `${API_URL}/api/communications`
        : `${API_URL}/api/communications?channel=${channelFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      logger.error('Error fetching logs:', error);
      toast.error('Failed to load communication logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads`, { credentials: 'include' });
      if (response.ok) setLeads(await response.json());
    } catch (error) {
      logger.error('Error fetching leads:', error);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        recipient_phone: formData.recipient_phone || null,
        recipient_email: formData.recipient_email || null,
        template_name: formData.template_name || null,
        subject: formData.subject || null
      };

      const response = await fetch(`${API_URL}/api/communications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Communication log created');
        setIsCreateOpen(false);
        resetForm();
        fetchLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create log');
      }
    } catch (error) {
      logger.error('Error creating log:', error);
      toast.error('Failed to create log');
    }
  };

  const resetForm = () => {
    setFormData({
      lead_id: '',
      channel: 'email',
      direction: 'outbound',
      subject: '',
      content: '',
      recipient_phone: '',
      recipient_email: '',
      template_name: '',
      status: 'sent'
    });
  };

  const filteredLogs = logs.filter(log => 
    log.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.subject?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.lead_id === leadId);
    return lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown';
  };

  const getChannelIcon = (channel) => {
    const ch = CHANNELS.find(c => c.value === channel);
    return ch?.icon || MessageSquare;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-in" data-testid="communications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Communications</h1>
          <p className="text-slate-500 font-body text-sm mt-1">Log and track all client communications</p>
        </div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-accent h-7 px-3 text-xs" data-testid="create-comm-btn">
              <Plus size={14} className="mr-1" />
              Log Communication
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Log Communication</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLog} className="space-y-4">
              <div>
                <Label htmlFor="lead_id">Lead *</Label>
                <Select value={formData.lead_id} onValueChange={(v) => setFormData({ ...formData, lead_id: v })} required>
                  <SelectTrigger data-testid="comm-lead">
                    <SelectValue placeholder="Select lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map(lead => (
                      <SelectItem key={lead.lead_id} value={lead.lead_id}>
                        {lead.first_name} {lead.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="channel">Channel *</Label>
                  <Select value={formData.channel} onValueChange={(v) => setFormData({ ...formData, channel: v })}>
                    <SelectTrigger data-testid="comm-channel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map(ch => (
                        <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="direction">Direction *</Label>
                  <Select value={formData.direction} onValueChange={(v) => setFormData({ ...formData, direction: v })}>
                    <SelectTrigger data-testid="comm-direction">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIRECTIONS.map(d => (
                        <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(formData.channel === 'email') && (
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="input-goldsands"
                    data-testid="comm-subject"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="content">Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  className="input-goldsands min-h-[100px]"
                  placeholder="Enter communication content..."
                  data-testid="comm-content"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {['whatsapp', 'sms', 'phone'].includes(formData.channel) && (
                  <div>
                    <Label htmlFor="recipient_phone">Recipient Phone</Label>
                    <Input
                      id="recipient_phone"
                      value={formData.recipient_phone}
                      onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                      className="input-goldsands"
                      placeholder="+1234567890"
                      data-testid="comm-phone"
                    />
                  </div>
                )}
                {formData.channel === 'email' && (
                  <div>
                    <Label htmlFor="recipient_email">Recipient Email</Label>
                    <Input
                      id="recipient_email"
                      type="email"
                      value={formData.recipient_email}
                      onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                      className="input-goldsands"
                      data-testid="comm-email"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="comm-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-accent" data-testid="comm-submit">
                  Log Communication
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters - Compact utility bar */}
      <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center bg-white border rounded-lg px-2 py-1.5 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
          <Input
            placeholder="Search communications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 h-7 text-xs border-slate-200"
            data-testid="search-comms"
          />
        </div>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="w-full md:w-36 h-7 text-xs" data-testid="filter-channel">
            <SelectValue placeholder="Filter by channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            {CHANNELS.map(ch => (
              <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Communications Table */}
      <Card className="card-goldsands">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Lead</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Direction</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No communication logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => {
                  const ChannelIcon = getChannelIcon(log.channel);
                  return (
                    <TableRow key={log.log_id} className="table-row-hover">
                      <TableCell className="font-medium">
                        {getLeadName(log.lead_id)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${channelColors[log.channel]} capitalize flex items-center gap-1 w-fit`}>
                          <ChannelIcon size={12} />
                          {log.channel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {log.direction}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          {log.subject && <p className="font-medium text-sm">{log.subject}</p>}
                          <p className="text-sm text-slate-500 truncate">{log.content}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
